import cn from 'classnames';
import type { ComponentProps } from 'react';
import { Progress } from '../progress';
import { Text } from '../typography';

type P = {
  size: 'default' | 'extra-small' | 'small';
} & Omit<ComponentProps<typeof Progress>, 'showInfo' | 'size' | 'type'>;

const milesStones = [0, 25, 50, 75, 100] as const;

const findClosestMilesStone = (percent: number): (typeof milesStones)[number] | null => {
  if (percent <= 25) {
    return null;
  }
  if (percent >= 100) {
    return 100;
  }

  for (const [index, milesStone] of milesStones.entries()) {
    if (milesStone > percent) {
      return index === 0 ? milesStones[0] : (milesStones[index - 1] ?? 0);
    }
  }

  return milesStones[0];
};

const ProgressWithMilestones = ({ ...rest }: P) => {
  const percentValue = rest.percent ?? 0;
  const closestMilesStone = findClosestMilesStone(percentValue);

  const progressTextClass = cn('progress-text', {
    'left-25': closestMilesStone === 25,
    'left-50': closestMilesStone === 50,
    'left-75': closestMilesStone === 75,
    'progress-text--at-100': closestMilesStone === 100,
  });

  return (
    <div className="progress-with-milestones">
      <Progress color="success" showInfo={false} {...rest} className="progress-with-milestones__progress" />
      {closestMilesStone ? (
        <div className={progressTextClass}>
          <Text size="small" weight="medium">
            {closestMilesStone}%
          </Text>
        </div>
      ) : null}
      {milesStones.map((milesStone) => (
        <div
          className={cn(`dot left-${milesStone}`, {
            'dot--current': milesStone === closestMilesStone,
            'dot--extra-small': rest.size === 'extra-small',
            'dot--green': milesStone <= percentValue,
            'dot--small': rest.size === 'small',
          })}
          key={milesStone}
        />
      ))}
    </div>
  );
};

export default ProgressWithMilestones;
